'use client';

import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAppNavigation } from '@practium/features/navigation';
import { useCreateHandoff } from '@practium/features/api';
import { UserRole } from '@practium/shared';
import { redirectToAdminWithHandoff } from '../services/adminHandoff';

interface UseAuthRedirectOptions {
	redirectTo?: string;
	redirectWhen?: 'authenticated' | 'unauthenticated';
	appContext?: 'admin' | 'client';
}

export const useAuthRedirect = ({
	redirectTo = '/',
	redirectWhen = 'authenticated',
	appContext,
}: UseAuthRedirectOptions = {}) => {
	const { isAuthenticated, isLoading, user } = useAuth();
	const { createAppLink } = useAppNavigation();
	const { mutateAsync: createHandoff } = useCreateHandoff();
	const hasNavigated = useRef(false);

	useEffect(() => {
		if (isLoading || hasNavigated.current) return;

		const shouldRedirect = redirectWhen === 'authenticated' ? isAuthenticated : !isAuthenticated;

		if (shouldRedirect) {
			hasNavigated.current = true;

			if (redirectWhen === 'authenticated' && appContext === 'client' && user?.role === UserRole.Admin) {
				redirectToAdminWithHandoff({ createHandoff, createAppLink });
				return;
			}

			if (typeof globalThis !== 'undefined') {
				globalThis.location.href = redirectTo;
			}
		}
	}, [isAuthenticated, isLoading, redirectTo, redirectWhen, appContext, user, createAppLink, createHandoff]);

	return { isLoading };
};
